import { useEffect } from 'react';
import { Outlet, useLoaderData, useMatches } from '@remix-run/react';
import { useSetAtom } from 'jotai';
import type { UIMatch } from '@remix-run/react';
import { useCurrentCategory } from '@rocco/ui/website-header/hooks/use-current-category';
import type { APHandle } from '~/modules/root';
import PageLayout from '@modules/professional/component/page-layout/PageLayout';
import { DetailPageButtons } from '@modules/business-manager/component/detail-page-buttons/DetailPageButtons';
import {
    PermissionEnum,
    type ProfessionalDetailsInitialFragment,
} from 'generated/graphql';
import type { Branch } from '~/modules/enquiry/util/extract-branch-from-network.server';
import type { Breadcrumb } from '@modules/professional/type/details-root.types';
import type { DropdownItem } from '@modules/enquiry/types';
import { useInView } from 'react-intersection-observer';
import { useSetEditMode } from '~/modules/root/hook/use-set-edit-mode';
import { useEditPermissions, usePermissions } from '~/modules/user';
import type { PrimaryCTAResult } from '@modules/professional/util/professional-cta-helper';
import { getBMPagePath } from '@modules/root/util/get-bm-page-path';
import type { ProfessionalBannersData } from '@modules/professional/page/OverviewPage';
import type { loader } from '@modules/professional/page/loaders/professional-page-loader.server';
import type { getPageLocalisation } from '~/modules/seo/api/get-page-localisation.server';
import { setCloneCountryStateAtom } from '~/state/ui-state-clone-country-link';

export interface ProfessionalDetailRootData {
    initial: ProfessionalDetailsInitialFragment;
    professionalBreadcrumb: Breadcrumb[];
    banners: ProfessionalBannersData;
    branchOptions: DropdownItem<Branch>[];
    localisation: Awaited<ReturnType<typeof getPageLocalisation>>;
    primaryCTA: PrimaryCTAResult;
    shouldIncludeLocalBusinessSchema: boolean;
    showHDEExhibitorMark: boolean;
}

export type ProfessionalDetailRootLoaderData = typeof loader;

const ProfessionalDetailRootPage = () => {
    const { initial, primaryCTA, awards } =
        useLoaderData<ProfessionalDetailRootLoaderData>();
    const { canEditProfile } = useEditPermissions(initial.ID);
    const canCopyLink = usePermissions(
        PermissionEnum.BmAnalyticsView,
        initial.ID,
    );
    const editLink = getBMPagePath(initial.URLSegment);
    useSetEditMode(editLink, canEditProfile);
    const [footerSectionRef, footerSectionInView] = useInView({
        threshold: 0.5,
    });

    const setCountryData = useSetAtom(setCloneCountryStateAtom);
    const { setCategory } = useCurrentCategory();

    // Set the atom value when the data loads
    useEffect(() => {
        setCountryData({
            urlSegment: initial?.URLSegment,
            currentCountry: initial?.Country,
            pageType: initial?.PageType,
            associatedPages: initial?.AssociatedPages,
        });
    }, [initial, setCountryData]);

    useEffect(() => {
        setCategory('Professionals');
        return () => setCategory(null);
    }, [setCategory]);

    const matches = useMatches();
    const shouldSkipLayout = matches.some(
        (match) =>
            (match as UIMatch<unknown, APHandle>)?.handle
                ?.skipProfessionalLayout === true,
    );

    // If a child route requests to skip the professional layout, render just the Outlet
    if (shouldSkipLayout) {
        return <Outlet />;
    }

    return (
        <PageLayout
            primaryCTA={primaryCTA}
            awards={awards}
            canCarouselControl={canEditProfile}
        >
            <DetailPageButtons
                id={initial.ID}
                editLink={editLink}
                shareableLink={`/professional/${initial.URLSegment}`}
                canEdit={canEditProfile}
                canCopyLink={canCopyLink}
                showButton={!footerSectionInView}
            />
            <Outlet />
            <div ref={footerSectionRef} />
        </PageLayout>
    );
};

export default ProfessionalDetailRootPage;
